// Single place for talking to the API - callers no longer build the base
// URL, set Content-Type, or attach the bearer token by hand (previously
// duplicated in every page's own fetch() calls). authFetch.js's global
// window.fetch patch still wraps every call made through here transparently,
// so silent 401 refresh-and-retry keeps working with no changes here.
const API_BASE = process.env.REACT_APP_BLADE_API_URL;

export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function request(path, { method = 'GET', body, headers = {}, raw = false } = {}) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { authorization: 'bearer ' + token } : {}),
      ...headers
    },
    body: body !== undefined ? JSON.stringify(body) : undefined
  });

  // Callers that need the raw Response (PDF blobs, SSE) handle it themselves.
  if (raw) return response;

  let data = null;
  const text = await response.text();
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }

  if (!response.ok) {
    const message = (data && data.message) || `Request failed (${response.status})`;
    throw new ApiError(message, response.status, data);
  }

  return data;
}

// File upload/download. The API takes raw bytes on an octet-stream body
// (not multipart, not base64 - see app/controllers/attachments.controller.js),
// so these can't go through request(), which always JSON-encodes.
async function upload(path, file) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/octet-stream',
      ...(token ? { authorization: 'bearer ' + token } : {})
    },
    body: file
  });

  let data = null;
  const text = await response.text();
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }
  if (!response.ok) {
    const message = (data && data.message) || `Upload failed (${response.status})`;
    throw new ApiError(message, response.status, data);
  }
  return data;
}

// Pulls a protected file down as a blob and hands the browser a save
// prompt. A plain <a href> can't be used for these - the download needs the
// bearer header, which only fetch can set.
async function download(path, filename) {
  const response = await request(path, { raw: true });
  if (!response.ok) {
    throw new ApiError(`Download failed (${response.status})`, response.status, null);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'download';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const apiClient = {
  get: (path, opts) => request(path, { ...opts, method: 'GET' }),
  post: (path, body, opts) => request(path, { ...opts, method: 'POST', body }),
  put: (path, body, opts) => request(path, { ...opts, method: 'PUT', body }),
  delete: (path, opts) => request(path, { ...opts, method: 'DELETE' }),
  raw: (path, opts) => request(path, { ...opts, raw: true }),
  upload,
  download
};
